import {
  Alert,
  Badge,
  Button,
  Checkbox,
  FileButton,
  Group,
  Modal,
  PasswordInput,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCloudUpload,
  IconDownload,
  IconFileImport,
  IconKey,
  IconListCheck,
  IconPlugConnected,
  IconRefresh,
  IconSettings,
  IconSitemap,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { WarningAlert } from '../forms/FormRecordParts.js';
import { errorCode } from '../personnel/source.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import classes from './ClassIntegration.module.css';
import type { ClassIntegrationSource } from './source.js';
import type {
  MadDownloadFormat,
  MadMapping,
  MadMappingImport,
  MadMappingItem,
  MadOverview,
  MadPreview,
  MadRange,
  MadRetraction,
  MadSettings,
  MadTransfer,
  MadVessel,
} from './types.js';

export interface ClassIntegrationScreenProps {
  source: ClassIntegrationSource;
}

type Tab = 'settings' | 'mapping' | 'preview' | 'transfers' | 'download';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; overview: MadOverview };

/** Kaydırılabilir tablo klavyeyle odaklanır (axe scrollable-region-focusable; WCAG 2.1.1). */
const SCROLL_VIEWPORT = { tabIndex: 0 } as const;
/** Hata kodunun metni: ekranın kendi kodları, yoksa genel metin. */
function useErrorText() {
  const { t, i18n } = useTranslation();
  return (code: string) =>
    i18n.exists(`classIntegration.errors.${code}`)
      ? t(`classIntegration.errors.${code}`)
      : t('classIntegration.errors.GENERIC');
}

/**
 * PMS ▸ Class Integration (DNV MAD; ortak 2026-10-28; ADR 0132): Settings, Mapping, Preview &
 * Validation, Transfers ve Download sekmeleri. İki anahtar girilmeden gönderim düğmeleri kapalıdır
 * ("API anahtarı bekleniyor"); önizleme ve indirme anahtarsız çalışır. Yalnız web ofiste.
 */
export function ClassIntegrationScreen({ source }: ClassIntegrationScreenProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [tab, setTab] = useState<Tab>('settings');
  const [vesselId, setVesselId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const overview = await source.overview();
      setState({ kind: 'ready', overview });
      setVesselId((current) => current ?? overview.vessels[0]?.vesselId ?? null);
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const header = (
    <PageHeader
      title={t('classIntegration.title')}
      description={t('classIntegration.description')}
    />
  );
  if (state.kind === 'loading') {
    return (
      <div className={classes.screen}>
        {header}
        <TableSkeleton columns={6} label={t('classIntegration.title')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={classes.screen}>
        {header}
        <Panel>
          <LoadError
            code={state.code}
            title={t('classIntegration.error')}
            onRetry={() => {
              setState({ kind: 'loading' });
              void load();
            }}
          />
        </Panel>
      </div>
    );
  }
  const { overview } = state;
  const vessel = overview.vessels.find((v) => v.vesselId === vesselId) ?? null;
  return (
    <div className={classes.screen}>
      {header}
      <ClassIntegrationStatus settings={overview.settings} />
      <Tabs
        value={tab}
        onChange={(value) => setTab((value as Tab) ?? 'settings')}
        keepMounted={false}
      >
        <Tabs.List className={classes.tabList}>
          <Tabs.Tab value="settings" leftSection={<IconSettings size={16} />}>
            {t('classIntegration.tabs.settings')}
          </Tabs.Tab>
          <Tabs.Tab value="mapping" leftSection={<IconSitemap size={16} />}>
            {t('classIntegration.tabs.mapping')}
          </Tabs.Tab>
          <Tabs.Tab value="preview" leftSection={<IconListCheck size={16} />}>
            {t('classIntegration.tabs.preview')}
          </Tabs.Tab>
          <Tabs.Tab value="transfers" leftSection={<IconCloudUpload size={16} />}>
            {t('classIntegration.tabs.transfers')}
          </Tabs.Tab>
          <Tabs.Tab value="download" leftSection={<IconDownload size={16} />}>
            {t('classIntegration.tabs.download')}
          </Tabs.Tab>
        </Tabs.List>
        {tab !== 'settings' ? (
          <div className={classes.toolbar}>
            <Select
              label={t('classIntegration.vessel')}
              placeholder={t('classIntegration.chooseVessel')}
              data={overview.vessels.map((v) => ({ value: v.vesselId, label: v.name }))}
              value={vesselId}
              onChange={setVesselId}
              allowDeselect={false}
              className={classes.vesselSelect}
            />
          </div>
        ) : null}
        <Tabs.Panel value="settings" className={classes.panel}>
          <SettingsTab overview={overview} source={source} onChanged={load} />
        </Tabs.Panel>
        {(['mapping', 'preview', 'transfers', 'download'] as const).map((name) => (
          <Tabs.Panel key={name} value={name} className={classes.panel}>
            {vessel ? (
              <VesselTab
                tab={name}
                vessel={vessel}
                settings={overview.settings}
                source={source}
                onChanged={load}
              />
            ) : (
              <Panel>
                <EmptyState title={t('classIntegration.noVessels')} />
              </Panel>
            )}
          </Tabs.Panel>
        ))}
      </Tabs>
    </div>
  );
}

/** Üst bant: anahtar yoksa "API anahtarı bekleniyor", kapalıysa bilgi. */
export function ClassIntegrationStatus({ settings }: { settings: MadSettings }) {
  const { t } = useTranslation();
  if (!settings.ready) {
    return (
      // Sarı Mantine başlığı açık zeminde kontrastı tutmaz (axe color-contrast; WCAG 1.4.3)
      <WarningAlert icon={<IconKey size={18} />} title={t('classIntegration.keysPending')}>
        {t('classIntegration.keysPendingText')}
      </WarningAlert>
    );
  }
  if (!settings.enabled) {
    return (
      <Alert color="blue" icon={<IconAlertTriangle size={18} />}>
        {t('classIntegration.disabledText')}
      </Alert>
    );
  }
  return null;
}

/* ---------- Settings ---------- */

function SettingsTab({
  overview,
  source,
  onChanged,
}: {
  overview: MadOverview;
  source: ClassIntegrationSource;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const { settings } = overview;
  const editable = overview.can.settings;
  const [form, setForm] = useState({
    enabled: settings.enabled,
    companyName: settings.companyName,
    endpoint: settings.endpoint,
    sendHourUtc: settings.sendHourUtc,
  });
  const [keys, setKeys] = useState({ subscriptionKey: '', accessToken: '' });
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<string | { ok: boolean; text: string }>) => {
    setBusy(true);
    setMessage(null);
    try {
      const outcome = await action();
      setMessage(typeof outcome === 'string' ? { ok: true, text: outcome } : outcome);
      await onChanged();
    } catch (error) {
      setMessage({ ok: false, text: errorText(errorCode(error)) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="md">
      {message ? (
        <Alert
          color={message.ok ? 'green' : 'red'}
          withCloseButton
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      ) : null}
      {!editable ? <Alert color="gray">{t('classIntegration.settings.readOnly')}</Alert> : null}
      <div className={classes.settingsGrid}>
        <Panel title={t('classIntegration.settings.connection')}>
          <Stack gap="sm">
            <Switch
              label={t('classIntegration.settings.enabled')}
              checked={form.enabled}
              disabled={!editable}
              onChange={(event) => setForm({ ...form, enabled: event.currentTarget.checked })}
            />
            <TextInput
              label={t('classIntegration.settings.companyName')}
              description={t('classIntegration.settings.companyHint')}
              value={form.companyName}
              maxLength={64}
              disabled={!editable}
              onChange={(event) => setForm({ ...form, companyName: event.currentTarget.value })}
            />
            <TextInput
              label={t('classIntegration.settings.source')}
              value={settings.source}
              readOnly
            />
            <TextInput
              label={t('classIntegration.settings.endpoint')}
              value={form.endpoint}
              disabled={!editable}
              onChange={(event) => setForm({ ...form, endpoint: event.currentTarget.value })}
            />
            <Select
              label={t('classIntegration.settings.sendHour')}
              data={Array.from({ length: 24 }, (_, hour) => ({
                value: String(hour),
                label: `${String(hour).padStart(2, '0')}:00`,
              }))}
              value={String(form.sendHourUtc)}
              disabled={!editable}
              allowDeselect={false}
              onChange={(value) => setForm({ ...form, sendHourUtc: Number(value ?? 2) })}
            />
            <Group justify="flex-end">
              <Button
                disabled={!editable}
                loading={busy}
                onClick={() =>
                  run(async () => {
                    await source.saveSettings({ rowVersion: settings.rowVersion, ...form });
                    return t('classIntegration.settings.saved');
                  })
                }
              >
                {t('classIntegration.settings.save')}
              </Button>
            </Group>
          </Stack>
        </Panel>
        <Panel title={t('classIntegration.settings.keys')}>
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              {t('classIntegration.settings.keysHint')}
            </Text>
            {(['subscriptionKey', 'accessToken'] as const).map((name) => (
              <div key={name}>
                <PasswordInput
                  label={t(`classIntegration.settings.${name}`)}
                  value={keys[name]}
                  disabled={!editable}
                  autoComplete="new-password"
                  onChange={(event) => setKeys({ ...keys, [name]: event.currentTarget.value })}
                />
                <Group gap="xs" mt={4}>
                  <KeyBadge state={settings.keys[name]} />
                  {settings.keys[name].set && editable ? (
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="red"
                      onClick={() =>
                        run(async () => {
                          await source.saveCredentials({ [name]: '' });
                          return t('classIntegration.settings.keysSaved');
                        })
                      }
                    >
                      {t('classIntegration.settings.removeKey')}
                    </Button>
                  ) : null}
                </Group>
              </div>
            ))}
            <Group justify="flex-end">
              <Button
                variant="default"
                leftSection={<IconPlugConnected size={16} />}
                disabled={!editable || !settings.ready}
                loading={busy}
                onClick={() =>
                  run(async () => {
                    const result = await source.testConnection();
                    return result.ok
                      ? t('classIntegration.settings.testOk', { status: result.status })
                      : {
                          ok: false,
                          text: t('classIntegration.settings.testFailed', {
                            code: result.errorCode ?? '',
                          }),
                        };
                  })
                }
              >
                {settings.ready
                  ? t('classIntegration.settings.test')
                  : t('classIntegration.keysPending')}
              </Button>
              <Button
                leftSection={<IconKey size={16} />}
                disabled={!editable || (keys.subscriptionKey === '' && keys.accessToken === '')}
                loading={busy}
                onClick={() =>
                  run(async () => {
                    await source.saveCredentials({
                      ...(keys.subscriptionKey ? { subscriptionKey: keys.subscriptionKey } : {}),
                      ...(keys.accessToken ? { accessToken: keys.accessToken } : {}),
                    });
                    setKeys({ subscriptionKey: '', accessToken: '' });
                    return t('classIntegration.settings.keysSaved');
                  })
                }
              >
                {t('classIntegration.settings.saveKeys')}
              </Button>
            </Group>
          </Stack>
        </Panel>
      </div>
      <Panel title={t('classIntegration.settings.vessels')}>
        <Text size="sm" c="dimmed" mb="sm">
          {t('classIntegration.settings.vesselsHint')}
        </Text>
        <VesselTable
          vessels={overview.vessels}
          editable={editable}
          onToggle={(vessel, enabled) =>
            run(async () => {
              await source.setVessel(vessel.vesselId, enabled, vessel.rowVersion);
              return t('classIntegration.settings.saved');
            })
          }
        />
      </Panel>
    </Stack>
  );
}

function KeyBadge({ state }: { state: MadSettings['keys']['subscriptionKey'] }) {
  const { t } = useTranslation();
  return state.set ? (
    <Badge color="green" variant="light" leftSection={<IconCircleCheck size={12} />}>
      {t('classIntegration.settings.keySet', { date: formatDateTime(state.updatedAt) })}
    </Badge>
  ) : (
    <Badge color="gray" variant="light">
      {t('classIntegration.settings.keyNotSet')}
    </Badge>
  );
}

export function VesselTable({
  vessels,
  editable,
  onToggle,
}: {
  vessels: MadVessel[];
  editable: boolean;
  onToggle: (vessel: MadVessel, enabled: boolean) => void;
}) {
  const { t } = useTranslation();
  if (vessels.length === 0) return <EmptyState title={t('classIntegration.noVessels')} />;
  return (
    <ScrollArea type="auto" viewportProps={SCROLL_VIEWPORT}>
      <Table striped highlightOnHover miw={640}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('classIntegration.vesselColumns.vessel')}</Table.Th>
            <Table.Th>{t('classIntegration.vesselColumns.imo')}</Table.Th>
            <Table.Th>{t('classIntegration.vesselColumns.send')}</Table.Th>
            <Table.Th>{t('classIntegration.vesselColumns.mapped')}</Table.Th>
            <Table.Th>{t('classIntegration.vesselColumns.pending')}</Table.Th>
            <Table.Th>{t('classIntegration.vesselColumns.last')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {vessels.map((vessel) => (
            <Table.Tr key={vessel.vesselId}>
              <Table.Td>{vessel.name}</Table.Td>
              <Table.Td>{vessel.imo ?? '—'}</Table.Td>
              <Table.Td>
                <Switch
                  aria-label={`${t('classIntegration.vesselColumns.send')} ${vessel.name}`}
                  checked={vessel.enabled}
                  disabled={!editable}
                  onChange={(event) => onToggle(vessel, event.currentTarget.checked)}
                />
              </Table.Td>
              <Table.Td>
                {vessel.coverage.mapped} / {vessel.coverage.classRelevant}
              </Table.Td>
              <Table.Td>{vessel.pending}</Table.Td>
              <Table.Td>
                {vessel.lastTransfer ? <TransferStatus transfer={vessel.lastTransfer} /> : '—'}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
}

/* ---------- Gemi sekmeleri ---------- */

function VesselTab({
  tab,
  vessel,
  settings,
  source,
  onChanged,
}: {
  tab: Exclude<Tab, 'settings'>;
  vessel: MadVessel;
  settings: MadSettings;
  source: ClassIntegrationSource;
  onChanged: () => Promise<void>;
}) {
  const [range, setRange] = useState<MadRange>({ from: null, to: null });
  if (tab === 'mapping')
    return <MappingTab vessel={vessel} source={source} onChanged={onChanged} />;
  if (tab === 'preview') {
    return <PreviewTab vessel={vessel} source={source} range={range} onRange={setRange} />;
  }
  if (tab === 'transfers') {
    return (
      <TransfersTab vessel={vessel} settings={settings} source={source} onChanged={onChanged} />
    );
  }
  return <DownloadTab vessel={vessel} source={source} range={range} onRange={setRange} />;
}

function useLoad<T>(load: () => Promise<T>) {
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; data: T }
  >({ kind: 'loading' });
  const reload = useCallback(async () => {
    try {
      setState({ kind: 'ready', data: await load() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [load]);
  useEffect(() => {
    setState({ kind: 'loading' });
    void reload();
  }, [reload]);
  return { state, reload };
}

function Loading<T>({
  state,
  reload,
  columns,
  children,
}: {
  state: ReturnType<typeof useLoad<T>>['state'];
  reload: () => Promise<void>;
  columns: number;
  children: (data: T) => ReactNode;
}) {
  const { t } = useTranslation();
  if (state.kind === 'loading') {
    return <TableSkeleton columns={columns} label={t('classIntegration.title')} />;
  }
  if (state.kind === 'error') {
    return (
      <Panel>
        <LoadError
          code={state.code}
          title={t('classIntegration.error')}
          onRetry={() => void reload()}
        />
      </Panel>
    );
  }
  return <>{children(state.data)}</>;
}

/* ---------- Mapping ---------- */

function MappingTab({
  vessel,
  source,
  onChanged,
}: {
  vessel: MadVessel;
  source: ClassIntegrationSource;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const load = useCallback(() => source.mapping(vessel.vesselId), [source, vessel.vesselId]);
  const { state, reload } = useLoad(load);
  const [report, setReport] = useState<{ file: Blob; result: MadMappingImport } | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const fail = (error: unknown) => setMessage({ ok: false, text: errorText(errorCode(error)) });
  return (
    <Loading state={state} reload={reload} columns={6}>
      {(mapping) => (
        <Stack gap="md">
          {message ? (
            <Alert
              color={message.ok ? 'green' : 'red'}
              withCloseButton
              onClose={() => setMessage(null)}
            >
              {message.text}
            </Alert>
          ) : null}
          <Panel>
            <Group justify="space-between" align="flex-start" gap="sm">
              <Stack gap={4} className={classes.grow}>
                <Text size="sm">{t('classIntegration.mapping.hint')}</Text>
                <Text size="sm" fw={600}>
                  {t('classIntegration.mapping.coverage', {
                    mapped: mapping.coverage.mapped,
                    total: mapping.coverage.classRelevant,
                  })}
                </Text>
              </Stack>
              <Group gap="xs">
                <Button
                  variant="default"
                  leftSection={<IconDownload size={16} />}
                  onClick={() => source.downloadMapping(vessel.vesselId).catch(fail)}
                >
                  {t('classIntegration.mapping.template')}
                </Button>
                {mapping.can.map ? (
                  <FileButton
                    accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(file) => {
                      if (!file) return;
                      source
                        .importMapping(vessel.vesselId, file, false)
                        .then((result) => setReport({ file, result }))
                        .catch(fail);
                    }}
                  >
                    {(props) => (
                      <Button leftSection={<IconFileImport size={16} />} {...props}>
                        {t('classIntegration.mapping.import')}
                      </Button>
                    )}
                  </FileButton>
                ) : null}
              </Group>
            </Group>
            {!mapping.can.map ? (
              <Text size="sm" c="dimmed" mt="sm">
                {t('classIntegration.mapping.readOnly')}
              </Text>
            ) : null}
          </Panel>
          <Panel>
            <MappingTable
              mapping={mapping}
              onSave={async (item, input) => {
                try {
                  await source.saveCode(vessel.vesselId, item.equipmentId, {
                    ...input,
                    rowVersion: item.rowVersion,
                  });
                  await reload();
                  await onChanged();
                } catch (error) {
                  fail(error);
                }
              }}
            />
          </Panel>
          <Modal
            opened={report !== null}
            onClose={() => setReport(null)}
            title={t('classIntegration.mapping.previewTitle')}
            size="lg"
          >
            {report ? (
              <MappingImportReport
                result={report.result}
                onApply={async () => {
                  try {
                    await source.importMapping(vessel.vesselId, report.file, true);
                    setReport(null);
                    setMessage({ ok: true, text: t('classIntegration.mapping.applied') });
                    await reload();
                    await onChanged();
                  } catch (error) {
                    setReport(null);
                    fail(error);
                  }
                }}
              />
            ) : null}
          </Modal>
        </Stack>
      )}
    </Loading>
  );
}

export function MappingTable({
  mapping,
  onSave,
}: {
  mapping: MadMapping;
  onSave: (
    item: MadMappingItem,
    input: { classComponent: string | null; visCode: string | null },
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [onlyClass, setOnlyClass] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, { classComponent: string; visCode: string }>>(
    {},
  );
  const items = mapping.items.filter((item) => !onlyClass || item.effectiveClassRelevant);
  const editable = mapping.can.map === true;
  return (
    <Stack gap="sm">
      <Checkbox
        label={t('classIntegration.mapping.onlyClass')}
        checked={onlyClass}
        onChange={(event) => setOnlyClass(event.currentTarget.checked)}
      />
      <ScrollArea type="auto" viewportProps={SCROLL_VIEWPORT}>
        <Table striped highlightOnHover miw={760}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('classIntegration.mapping.columns.code')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.name')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.sfi')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.classRelevant')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.classComponent')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.visCode')}</Table.Th>
              {editable ? <Table.Th /> : null}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => {
              const draft = drafts[item.equipmentId] ?? {
                classComponent: item.classComponent ?? '',
                visCode: item.visCode ?? '',
              };
              const changed =
                draft.classComponent !== (item.classComponent ?? '') ||
                draft.visCode !== (item.visCode ?? '');
              const inherited =
                !item.classComponent && item.inheritedClassComponent
                  ? t('classIntegration.mapping.inherited', {
                      code: item.inheritedClassComponent,
                    })
                  : undefined;
              const set = (patch: Partial<typeof draft>) =>
                setDrafts({ ...drafts, [item.equipmentId]: { ...draft, ...patch } });
              return (
                <Table.Tr key={item.equipmentId}>
                  <Table.Td>{item.code}</Table.Td>
                  <Table.Td>{item.name}</Table.Td>
                  <Table.Td>{item.sfiCode ?? '—'}</Table.Td>
                  <Table.Td>
                    {item.effectiveClassRelevant ? (
                      <Badge variant="light" color="blue">
                        {t('classIntegration.mapping.columns.classRelevant')}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </Table.Td>
                  <Table.Td>
                    {editable ? (
                      <TextInput
                        size="xs"
                        aria-label={`${t('classIntegration.mapping.columns.classComponent')} ${item.code}`}
                        value={draft.classComponent}
                        placeholder={inherited}
                        maxLength={64}
                        onChange={(event) => set({ classComponent: event.currentTarget.value })}
                      />
                    ) : (
                      (item.classComponent ?? inherited ?? '—')
                    )}
                  </Table.Td>
                  <Table.Td>
                    {editable ? (
                      <TextInput
                        size="xs"
                        aria-label={`${t('classIntegration.mapping.columns.visCode')} ${item.code}`}
                        value={draft.visCode}
                        maxLength={128}
                        onChange={(event) => set({ visCode: event.currentTarget.value })}
                      />
                    ) : (
                      (item.visCode ?? '—')
                    )}
                  </Table.Td>
                  {editable ? (
                    <Table.Td>
                      <Button
                        size="compact-sm"
                        variant="light"
                        disabled={!changed}
                        onClick={async () => {
                          await onSave(item, {
                            classComponent: draft.classComponent.trim() || null,
                            visCode: draft.visCode.trim() || null,
                          });
                          const { [item.equipmentId]: _saved, ...rest } = drafts;
                          setDrafts(rest);
                        }}
                      >
                        {t('classIntegration.mapping.save')}
                      </Button>
                    </Table.Td>
                  ) : null}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Stack>
  );
}

export function MappingImportReport({
  result,
  onApply,
}: {
  result: MadMappingImport;
  onApply: () => void;
}) {
  const { t } = useTranslation();
  const changes = result.rows.filter((row) => row.action !== 'unchanged');
  return (
    <Stack gap="sm">
      {result.errors.length > 0 ? (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('classIntegration.mapping.columns.row')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.code')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.error')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {result.errors.map((error) => (
              <Table.Tr key={`${error.row}-${error.column}-${error.code}`}>
                <Table.Td>{error.row}</Table.Td>
                <Table.Td>{error.column ?? '—'}</Table.Td>
                <Table.Td>
                  <IssueText code={error.code} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : changes.length === 0 ? (
        <Text size="sm">{t('classIntegration.mapping.nothing')}</Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('classIntegration.mapping.columns.row')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.code')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.classComponent')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.visCode')}</Table.Th>
              <Table.Th>{t('classIntegration.mapping.columns.action')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {changes.map((row) => (
              <Table.Tr key={row.row}>
                <Table.Td>{row.row}</Table.Td>
                <Table.Td>{row.equipmentCode}</Table.Td>
                <Table.Td>{row.classComponent ?? '—'}</Table.Td>
                <Table.Td>{row.visCode ?? '—'}</Table.Td>
                <Table.Td>{t(`classIntegration.mapping.actions.${row.action}`)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <Group justify="flex-end">
        <Button disabled={result.errors.length > 0 || changes.length === 0} onClick={onApply}>
          {t('classIntegration.mapping.apply')}
        </Button>
      </Group>
    </Stack>
  );
}

function IssueText({ code }: { code: string }) {
  const { t, i18n } = useTranslation();
  return (
    <>
      {i18n.exists(`classIntegration.issues.${code}`) ? t(`classIntegration.issues.${code}`) : code}
    </>
  );
}

/* ---------- Preview & Validation ---------- */

/**
 * Son 12 ay (DNV-CP-0206 Sec.2 [4.5] b; ADR 0133 §1): geçen yılın aynı gününden bugüne, iki uç
 * dahil. 29 Şubat geçen yılda yoksa 28 Şubat. Gün UTC (MAD tarihleri gün olarak gider).
 */
export function lastTwelveMonths(today: string): MadRange {
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const last = new Date(Date.UTC(y - 1, m, 0)).getUTCDate();
  const from = `${y - 1}-${String(m).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
  return { from, to: today };
}

const todayUtc = () => new Date().toISOString().slice(0, 10);

function RangeInputs({ range, onRange }: { range: MadRange; onRange: (range: MadRange) => void }) {
  const { t } = useTranslation();
  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Button variant="default" onClick={() => onRange(lastTwelveMonths(todayUtc()))}>
        {t('classIntegration.preview.last12')}
      </Button>
      <TextInput
        type="date"
        label={t('classIntegration.preview.from')}
        value={range.from ?? ''}
        onChange={(event) => onRange({ ...range, from: event.currentTarget.value || null })}
      />
      <TextInput
        type="date"
        label={t('classIntegration.preview.to')}
        value={range.to ?? ''}
        onChange={(event) => onRange({ ...range, to: event.currentTarget.value || null })}
      />
    </Group>
  );
}

function PreviewTab({
  vessel,
  source,
  range,
  onRange,
}: {
  vessel: MadVessel;
  source: ClassIntegrationSource;
  range: MadRange;
  onRange: (range: MadRange) => void;
}) {
  const load = useCallback(
    () => source.preview(vessel.vesselId, range),
    [source, vessel.vesselId, range],
  );
  const { state, reload } = useLoad(load);
  return (
    <Stack gap="md">
      <Panel>
        <RangeInputs range={range} onRange={onRange} />
      </Panel>
      <Loading state={state} reload={reload} columns={7}>
        {(preview) => <PreviewBody preview={preview} />}
      </Loading>
    </Stack>
  );
}

export function PreviewBody({ preview }: { preview: MadPreview }) {
  const { t } = useTranslation();
  return (
    <Stack gap="md">
      <div className={classes.stats}>
        <StatTile
          icon={<IconListCheck size={20} />}
          label={t('classIntegration.preview.records')}
          value={preview.summary.records}
        />
        <StatTile
          icon={<IconAlertTriangle size={20} />}
          label={t('classIntegration.preview.errors')}
          value={preview.summary.errors}
          tone={preview.summary.errors > 0 ? 'danger' : 'default'}
        />
        <StatTile
          icon={<IconAlertTriangle size={20} />}
          label={t('classIntegration.preview.warnings')}
          value={preview.summary.warnings}
          tone={preview.summary.warnings > 0 ? 'warning' : 'default'}
        />
        <StatTile
          icon={<IconCircleCheck size={20} />}
          label={t('classIntegration.preview.sent')}
          value={preview.summary.sent}
          tone="success"
        />
      </div>
      <Panel>
        <Text size="sm" c="dimmed" mb="sm">
          {t('classIntegration.preview.hint')}
        </Text>
        {preview.entries.length === 0 ? (
          <EmptyState title={t('classIntegration.preview.empty')} />
        ) : (
          <ScrollArea type="auto" viewportProps={SCROLL_VIEWPORT}>
            <Table striped highlightOnHover miw={900}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('classIntegration.preview.columns.wo')}</Table.Th>
                  <Table.Th>{t('classIntegration.preview.columns.done')}</Table.Th>
                  <Table.Th>{t('classIntegration.preview.columns.equipment')}</Table.Th>
                  <Table.Th>{t('classIntegration.preview.columns.jobno')}</Table.Th>
                  <Table.Th>{t('classIntegration.preview.columns.jobname')}</Table.Th>
                  <Table.Th>{t('classIntegration.preview.columns.classcomponent')}</Table.Th>
                  <Table.Th>{t('classIntegration.preview.columns.status')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {preview.entries.map((entry) => (
                  <Table.Tr key={entry.recordId}>
                    <Table.Td>{entry.woNo ?? '—'}</Table.Td>
                    <Table.Td>{formatDay(entry.doneOn)}</Table.Td>
                    <Table.Td>{entry.equipmentCode}</Table.Td>
                    <Table.Td>{String(entry.task.jobno ?? '')}</Table.Td>
                    <Table.Td className={classes.wrap}>{String(entry.task.jobname ?? '')}</Table.Td>
                    <Table.Td>{String(entry.task.classcomponent ?? '') || '—'}</Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        {entry.sent ? (
                          <Badge color="green" variant="light">
                            {t('classIntegration.preview.sentBadge')}
                          </Badge>
                        ) : null}
                        {entry.issues.length === 0 && !entry.sent ? (
                          <Badge color="blue" variant="light">
                            {t('classIntegration.preview.ok')}
                          </Badge>
                        ) : null}
                        {entry.issues.map((issue) => (
                          <Text
                            key={`${issue.field}-${issue.code}`}
                            size="xs"
                            className={
                              issue.severity === 'error' ? classes.issueError : classes.issueWarning
                            }
                          >
                            {issue.field ? `${issue.field}: ` : ''}
                            <IssueText code={issue.code} />
                          </Text>
                        ))}
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Panel>
    </Stack>
  );
}

/* ---------- Transfers ---------- */

function TransferStatus({ transfer }: { transfer: MadTransfer }) {
  const { t } = useTranslation();
  const color =
    transfer.status === 'succeeded'
      ? 'green'
      : transfer.status === 'failed'
        ? 'red'
        : transfer.status === 'running'
          ? 'blue'
          : 'gray';
  return (
    <Badge color={color} variant="light">
      {t(`classIntegration.transfers.status.${transfer.status}`)}
    </Badge>
  );
}

function TransfersTab({
  vessel,
  settings,
  source,
  onChanged,
}: {
  vessel: MadVessel;
  settings: MadSettings;
  source: ClassIntegrationSource;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const load = useCallback(() => source.transfers(vessel.vesselId), [source, vessel.vesselId]);
  const { state, reload } = useLoad(load);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const send = async (retry: boolean) => {
    setBusy(true);
    setMessage(null);
    try {
      const run = await source.send(vessel.vesselId, retry);
      setMessage({
        ok: run.status !== 'failed',
        text: t('classIntegration.transfers.sentResult', {
          status: t(`classIntegration.transfers.status.${run.status}`),
        }),
      });
      await reload();
      await onChanged();
    } catch (error) {
      setMessage({ ok: false, text: errorText(errorCode(error)) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Loading state={state} reload={reload} columns={8}>
      {(data) => (
        <TransfersBody
          transfers={data.transfers}
          retractions={data.retractions}
          ready={settings.ready}
          busy={busy}
          message={message}
          onDismiss={() => setMessage(null)}
          onSend={send}
        />
      )}
    </Loading>
  );
}

export function TransfersBody({
  transfers,
  retractions,
  ready,
  busy = false,
  message = null,
  onDismiss,
  onSend,
}: {
  transfers: MadTransfer[];
  retractions: MadRetraction[];
  ready: boolean;
  busy?: boolean;
  message?: { ok: boolean; text: string } | null;
  onDismiss?: () => void;
  onSend: (retry: boolean) => void;
}) {
  const { t } = useTranslation();
  const lastFailed = transfers[0]?.status === 'failed';
  return (
    <Stack gap="md">
      {message ? (
        <Alert
          color={message.ok ? 'green' : 'red'}
          withCloseButton
          {...(onDismiss ? { onClose: onDismiss } : {})}
        >
          {message.text}
        </Alert>
      ) : null}
      <Panel>
        <Group justify="flex-end" gap="xs" mb="sm">
          {lastFailed ? (
            <Button
              variant="default"
              leftSection={<IconRefresh size={16} />}
              disabled={!ready}
              loading={busy}
              onClick={() => onSend(true)}
            >
              {ready ? t('classIntegration.transfers.retry') : t('classIntegration.keysPending')}
            </Button>
          ) : null}
          <Button
            leftSection={ready ? <IconCloudUpload size={16} /> : <IconKey size={16} />}
            disabled={!ready}
            loading={busy}
            onClick={() => onSend(false)}
          >
            {ready ? t('classIntegration.transfers.send') : t('classIntegration.keysPending')}
          </Button>
        </Group>
        {transfers.length === 0 ? (
          <EmptyState title={t('classIntegration.transfers.empty')} />
        ) : (
          <ScrollArea type="auto" viewportProps={SCROLL_VIEWPORT}>
            <Table striped highlightOnHover miw={900}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('classIntegration.transfers.columns.started')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.trigger')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.status')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.records')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.batches')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.http')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.error')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.next')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {transfers.map((transfer) => (
                  <Table.Tr key={transfer.id}>
                    <Table.Td>{formatDateTime(transfer.startedAt)}</Table.Td>
                    <Table.Td>
                      {t(`classIntegration.transfers.trigger.${transfer.trigger}`)}
                    </Table.Td>
                    <Table.Td>
                      <TransferStatus transfer={transfer} />
                    </Table.Td>
                    <Table.Td>
                      {transfer.sentCount} / {transfer.taskCount}
                      {transfer.skippedCount > 0 ? ` (+${transfer.skippedCount})` : ''}
                    </Table.Td>
                    <Table.Td>{transfer.batchCount}</Table.Td>
                    <Table.Td>{transfer.httpStatus ?? '—'}</Table.Td>
                    <Table.Td className={classes.wrap}>
                      {transfer.errorCode
                        ? `${transfer.errorCode}${transfer.errorDetail ? `: ${transfer.errorDetail}` : ''}`
                        : '—'}
                    </Table.Td>
                    <Table.Td>
                      {transfer.nextAttemptAt ? formatDateTime(transfer.nextAttemptAt) : '—'}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Panel>
      {retractions.length > 0 ? (
        <Panel title={t('classIntegration.transfers.retractions')}>
          <Text size="sm" c="dimmed" mb="sm">
            {t('classIntegration.transfers.retractionsHint')}
          </Text>
          <ScrollArea type="auto" viewportProps={SCROLL_VIEWPORT}>
            <Table striped miw={640}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('classIntegration.transfers.columns.record')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.sentAt')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.voidedAt')}</Table.Th>
                  <Table.Th>{t('classIntegration.transfers.columns.reason')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {retractions.map((r) => (
                  <Table.Tr key={r.recordId}>
                    <Table.Td>{r.recordId.slice(-12)}</Table.Td>
                    <Table.Td>{formatDateTime(r.sentAt)}</Table.Td>
                    <Table.Td>{formatDateTime(r.voidedAt)}</Table.Td>
                    <Table.Td className={classes.wrap}>{r.reason}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Panel>
      ) : null}
    </Stack>
  );
}

/* ---------- Download ---------- */

function DownloadTab({
  vessel,
  source,
  range,
  onRange,
}: {
  vessel: MadVessel;
  source: ClassIntegrationSource;
  range: MadRange;
  onRange: (range: MadRange) => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [busy, setBusy] = useState<MadDownloadFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const download = async (format: MadDownloadFormat, chosen: MadRange = range) => {
    setBusy(format);
    setError(null);
    try {
      await source.download(vessel.vesselId, format, chosen);
    } catch (failure) {
      setError(errorText(errorCode(failure)));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Panel>
      <Stack gap="md">
        {error ? <Alert color="red">{error}</Alert> : null}
        <Text size="sm">{t('classIntegration.download.hint')}</Text>
        <Group gap="sm" align="center" wrap="wrap">
          <Button
            leftSection={<IconDownload size={16} />}
            disabled={busy !== null}
            onClick={() => {
              const last = lastTwelveMonths(todayUtc());
              onRange(last);
              void download('json', last);
            }}
          >
            {t('classIntegration.download.last12')}
          </Button>
          <Text size="xs" c="dimmed">
            {t('classIntegration.download.last12Hint')}
          </Text>
        </Group>
        <RangeInputs range={range} onRange={onRange} />
        <Group gap="sm">
          {(['json', 'xlsx', 'csv'] as const).map((format) => (
            <Button
              key={format}
              variant={format === 'json' ? 'filled' : 'default'}
              leftSection={<IconDownload size={16} />}
              loading={busy === format}
              onClick={() => download(format)}
            >
              {t(`classIntegration.download.${format}`)}
            </Button>
          ))}
        </Group>
      </Stack>
    </Panel>
  );
}
